import React from 'react';
import {Platform, StyleSheet, Pressable, View} from 'react-native';
import Localized from 'src/constants/AppStrings';
import Styles from '../../Styles';
import {ScreenProps} from 'src/types/Screen';
import Settings from 'src/Settings';
import AccountStore from 'src/stores/AccountStore';
import {GiftItem} from 'src/types/Snack';
import AVText from './../AVText';
import {getDescriber} from './../descriptor/DescriptorType';
import UnReadlIcon from 'src/components/img/svg/UnReadIcon';
import {getNotificationDescriber} from 'src/components/elements/notificationDescriptor/DescriptorType';
import ChevronRight from '../../img/svg/ChevronRight';
import Swipeable from 'react-native-gesture-handler/ReanimatedSwipeable';
import RenderRightDelectActions from 'src/components/elements/RenderRightDelectActions';
import {useDeleteSwipe} from '../hooks';
import {
  withGlobalize,
  WithGlobalizeProps,
} from '../../../i18n/HOC/withGlobalize';
import {useGlobalize} from '../../../i18n/hook/useGlobalize';
const margin = Styles.Spacing.m3 + Styles.Spacing.m2;

const SnackItemNewUI = ({
  snack,
  onPress,
  onDelete,
  globalize,
}: ScreenProps &
  WithGlobalizeProps & {
    snack: GiftItem;
    onDelete?: () => void;
    onPress: () => void;
  }) => {
  const {Labels} = Localized;
  const describerprops = {
    snack,
    globalize,
  };

  const {formatCurrency} = useGlobalize();
  const [
    maxSwipeDistance,
    screenWidth,
    swipeableRef,
    handleSwipeStart,
    handleSwipeEnd,
    handlePress,
  ] = useDeleteSwipe({onPress});

  const renderDescription = (isSender: boolean) => {
    return isSender
      ? Labels.your_snack_sent
      : Labels.formatString(Labels.sent_you_snack, snack.senderName);
  };

  const renderMessage = (isSender: boolean) => {
    return isSender
      ? Labels.formatString(
          Labels.we_have_sent_your_snack,
          formatCurrency(snack.amount, AccountStore.getCurrency(), {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          }),
          snack.email,
        )
      : getDescriber().snackMessageDescriptor(describerprops);
  };

  const isSender = snack.senderAccountId === AccountStore.getAccountId();
  const styles = StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'row',
      marginHorizontal: margin,
      marginTop: Styles.Spacing.m2,
      marginBottom: Styles.Spacing.m2,
      paddingHorizontal: Styles.Spacing.m3,
      paddingVertical: Styles.Spacing.m3,
      justifyContent: 'center',
    },
    icon: {
      alignItems: 'center',
      borderRadius: 32,
      backgroundColor: Styles.darkBlueRevolveColor,
      width: 50,
      justifyContent: 'center',
      height: 50,
    },
    message: {
      flex: 1,
      color: Styles.black,
      fontFamily: Styles.FontFamily.figtreeRegular,
      fontSize: Styles.Fonts.f8,
    },
    qrCodeImage: {
      width: 53,
      height: 48,
    },
    messageLeft: {
      flex: 1,
      marginLeft: Styles.Spacing.m2 + Styles.Spacing.m1,
    },
    containerVertical: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    topRight: {
      left: 10,
      flexDirection: 'row',
      justifyContent: 'flex-end',
    },
    topRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    arrow: {
      width: 24,
      height: 24,
      alignItems: 'center',
      alignSelf: 'center',
      justifyContent: 'center',
    },
    from: {
      color: Styles.darkColor,
      fontSize: Styles.Fonts.f7,
      fontWeight: 'bold',
      fontFamily: Styles.FontFamily.figtreeRegular,
    },
    commonRow: {
      flexDirection: 'row',
    },
    titleHeaderView: {
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    refiveStyle: {
      borderBottomWidth: 1,
      borderBottomColor: '#9A9A9F',
      alignSelf: 'center',
    },
    canteenUnread: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: Styles.Spacing.m2,
    },
  });

  return (
    <Swipeable
      enabled={!!onDelete}
      onSwipeableWillOpen={handleSwipeStart}
      onSwipeableClose={handleSwipeEnd}
      ref={swipeableRef}
      renderRightActions={(progressAnimatedValue, dragAnimatedValue) => (
        <RenderRightDelectActions
          dragAnimatedValue={dragAnimatedValue}
          onPress={() => {
            swipeableRef.current?.close();
            onDelete();
          }}
        />
      )}
      containerStyle={{
        width: Platform.OS === 'web' ? undefined : screenWidth,
      }}
      rightThreshold={maxSwipeDistance}
      overshootRight={false}
    >
      <Pressable
        onPress={handlePress}
        style={[
          getDescriber().getSnackItemStyleDescriptor().container,
          getDescriber().getSnackItemStyleDescriptor().containerBackground,
          !Settings.isCanteen() && Styles.Style.shadow,
          !Settings.isRefiveAnd365Pay() && styles.refiveStyle,
        ]}
        accessible
        accessibilityLabel="Inbox Snack Item"
        testID="qa_btn_InboxSnackItem"
        nativeID="qa_btn_InboxSnackItem"
      >
        {getDescriber().snackItemGiftBoxTemplateDescriptor(describerprops)}
        <View style={getDescriber().getSnackItemStyleDescriptor().messageLeft}>
          <View style={[styles.titleHeaderView]}>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
              style={getDescriber().getSnackItemStyleDescriptor().titleHeader}
              accessible
              testID="qa_NotificationType_txt"
              nativeID="qa_NotificationType_txt"
            >
              {Localized.Buttons.send_a_snack}
            </AVText>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
              style={[
                getDescriber().getSnackItemStyleDescriptor().date,
                styles.commonRow,
              ]}
              accessible
              testID="qa_MessageDate_txt"
              nativeID="qa_MessageDate_txt"
            >
              {getDescriber().snackItemDateDescriptor(describerprops)}
            </AVText>
          </View>
          {Settings.isRefiveAnd365Pay() ? (
            <AVText
              style={getDescriber().getSnackItemStyleDescriptor().subject}
              numberOfLines={getDescriber().snackItemNumberDescriptor()}
              accessible
              testID="qa_MessageSubject_txt"
              nativeID="qa_MessageSubject_txt"
            >
              {renderDescription(isSender)}
            </AVText>
          ) : (
            <View style={styles.canteenUnread}>
              {!snack.isRead && (
                <UnReadlIcon
                  color={getNotificationDescriber().getUnreadMessageIconColor()}
                />
              )}
              <AVText
                style={getDescriber().getSnackItemStyleDescriptor().subject}
                numberOfLines={getDescriber().snackItemNumberDescriptor()}
                accessible
                testID="qa_MessageSubject_txt"
                nativeID="qa_MessageSubject_txt"
              >
                {renderDescription(isSender)}
              </AVText>
            </View>
          )}

          <AVText
            style={getDescriber().getSnackItemStyleDescriptor().message}
            numberOfLines={getDescriber().snackItemNumberDescriptor()}
            accessible
            testID="qa_MessageBody_txt"
            nativeID="qa_MessageBody_txt"
          >
            {renderMessage(isSender)}
          </AVText>
        </View>
        {Settings.isRefiveAnd365Pay() && (
          <View
            style={getDescriber().getSnackItemStyleDescriptor().chevronRight}
            accessible
            accessibilityLabel="Snack Item Icon"
          >
            <ChevronRight />
          </View>
        )}
      </Pressable>
    </Swipeable>
  );
};

export default withGlobalize(SnackItemNewUI);
